import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import DropDown from 'components/Dropdown';
import Image from 'components/image/Image';
import JobOpeningCard from 'components/jobOpeningCard';
import Autocomplete from 'components/shared/Autocomplete/Index';
import { workExperience } from 'core/constants/preferenceConstats';
import { RootState } from 'redux/store';
import { JobService } from 'services/jobs/job';
import useLocales from 'hooks/useLocales';
import JobOpenings from 'components/jobOpenings';
import Button from 'components/Button/Index';
import { Slider } from '@mui/material';
import { IndustryRedux } from 'core/interface/education';
import { jobContracts } from 'core/constants/workExperience';
import { getSalaryByRange } from 'constants/constants';
import { CMSService } from 'services/cms/cms';
import { IMe } from 'core/interface/me';
import { useNavigate, useParams } from 'react-router';
import { useDebounce } from 'helpers/useDebounce';
import { ProfileService } from 'services/profile/profile';
import colors from 'assets/variables/colors';
import PopUpWrapper from 'components/PopUpWrapper';
import useAuth from 'hooks/useAuth';
import { CANDIDATE_ROUTES } from 'core/constants/candidateRoutes';
import { getJobIndustryIds } from 'utils/jobIndustriesLocations';

const EMPTY_INDUSTRIES: never[] = [];

interface City {
  id: number;
  display_name: string;
}

interface FilterData {
  industry?: number | undefined;
  location?: City;
  required_experience?: string;
  type?: string;
  range?: [number, number];
}

interface Job {
  is_public: boolean;
  industries?: Array<number | { id: number; name?: string }>;
  locations?: Array<number | { id: number; display_name?: string }>;
  work_exp: number;
  contract_type: number;
  salary: any;
}

const getLocationId = (location: number | { id: number }): number =>
  typeof location === 'object' ? Number(location.id) : Number(location);

function JobOpening({ canEdit }: { canEdit: boolean }) {
  const { isAuthenticated, isCandidate } = useAuth();
  const { t } = useLocales();
  const { cities } = useSelector((state: RootState) => state.locations);
  const [formValues, setFormValues] = useState<any>({});
  const [value, setValue] = useState<any[]>([0, 5000]);
  const [openModal, setOpenModal] = useState(false);
  const { cms } = useSelector((state: RootState) => state.cms);

  const publicJobs = React.useMemo(() => {
    if (!cms?.jobs?.length) return [];
    // When not logged in, assume all jobs are public if is_public property is missing
    return cms.jobs.filter(
      (job: any) => job.is_public === undefined || job.is_public
    );
  }, [cms?.jobs]);
  const [filteredJobs, setFilteredJobs] = useState<any[]>([]);

  useEffect(() => {
    setFilteredJobs(publicJobs);
  }, [publicJobs]);

  const industries = useSelector(
    (selector: IndustryRedux) =>
      selector.industries?.industry ?? EMPTY_INDUSTRIES
  );

  const me = useSelector((state: IMe) => state.user).user;

  const [filterData, setFilterData] = useState<FilterData>({});
  const { id } = useParams();
  const navigate = useNavigate();

  const [locationQuery, setLocationQuery] = useState<string>('');
  const [industryQuery, setIndustryQuery] = useState<string>('');
  const debouncedLocationQuery = useDebounce(locationQuery, 500);
  const debouncedIndustryQuery = useDebounce(industryQuery, 500);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  useEffect(() => {
    const filtered = publicJobs?.filter((job: Job) => {
      const rangeValue = getSalaryByRange(job.salary);
      const { industry, location, required_experience, type, range } =
        filterData;

      const industryMatch =
        industry === undefined ||
        getJobIndustryIds(job.industries).includes(Number(industry));
      const locationMatch =
        !location ||
        (job.locations &&
          job.locations.some(
            (jobLocation) =>
              getLocationId(jobLocation) === Number(filterData?.location?.id)
          ));
      const experienceMatch =
        !required_experience || job.work_exp === Number(required_experience);
      const typeMatch = !type || job?.contract_type === Number(type);
      const salaryMatch =
        !range ||
        (rangeValue &&
          rangeValue?.min >= range[0] &&
          rangeValue?.max <= range[1]);

      return (
        industryMatch &&
        locationMatch &&
        experienceMatch &&
        typeMatch &&
        salaryMatch
      );
    });

    setFilteredJobs(filtered || publicJobs);
  }, [publicJobs, filterData]);

  const handleModal = () => {
    setOpenModal(!openModal);
  };

  useEffect(() => {
    if (!debouncedLocationQuery.trim()) {
      return;
    }
    setIsLoadingLocations(true);
    setLocationError(null);
    JobService.getLocations({
      q: debouncedLocationQuery
    })
      .catch((error) => {
        setLocationError('Failed to load locations. Please try again.');
        console.error('Error loading locations:', error);
      })
      .finally(() => {
        setIsLoadingLocations(false);
      });
  }, [debouncedLocationQuery]);

  useEffect(() => {
    if (debouncedIndustryQuery.trim()) {
      ProfileService.getIndustries(debouncedIndustryQuery);
    }
  }, [debouncedIndustryQuery]);

  const handleChange = (companyData: any) => {
    const formData = new FormData();

    for (const key in companyData) {
      if (key !== 'id') {
        formData.append(key, (companyData as any)[key]);
      }
    }
    CMSService.updateCMS(me?.company, formData);
    handleModal();
  };

  const handleLocationChange = (displayName: string) => {
    setLocationQuery(displayName);
    if (!displayName) {
      setFilterData((prev) => ({ ...prev, location: undefined }));
      return;
    }

    const matchingCity = cities?.find(
      (city: City) =>
        city.display_name.toLowerCase() === displayName.toLowerCase()
    );

    setFilterData((prev) => ({
      ...prev,
      location: matchingCity
    }));
  };

  const handleLoadMoreCities = () => {
    if (!isLoadingLocations && cities?.length) {
      setIsLoadingLocations(true);
      const params = {
        limit: 10,
        offset: cities.length,
        q: debouncedLocationQuery || undefined
      };
      JobService.getLocations(params, true)
        .catch((error) => {
          setLocationError('Failed to load more locations. Please try again.');
          console.error('Error loading more locations:', error);
        })
        .finally(() => {
          setIsLoadingLocations(false);
        });
    }
  };

  const handleShowAllJobs = () => {
    if (!isAuthenticated) {
      navigate('/jobs');
    } else {
      if (isCandidate) {
        navigate(`${CANDIDATE_ROUTES.FIND_JOBS}`);
      } else {
        navigate(`/jobs?company=${cms?.company}`);
      }
    }
  };

  return (
    <>
      <div className="jobOpeningContainer">
        <h1>{t('Job openings')}</h1>
        {canEdit && (
          <div className="pencil_icon" onClick={handleModal}>
            <div className="pencil_circle" />
            <Image
              className="pencil"
              name="pencil.svg"
              width={13}
              height={13}
            />
          </div>
        )}
      </div>
      <div className="filterContainer">
        <Formik
          enableReinitialize
          initialValues={formValues}
          onSubmit={(values, actions) => {}}
        >
          {({ setFieldValue, values }) => (
            <Form>
              <div className="containerJob">
                <div className="fields_flex">
                  <div className="inputContainer">
                    <span className="label">{t('Specialty')}</span>
                    <Autocomplete
                      fieldName="industry"
                      show_arrow={false}
                      value={
                        filterData?.industry
                          ? industries.find(
                              (industry) =>
                                String(industry.id) ===
                                String(filterData?.industry)
                            )?.name || ''
                          : ''
                      }
                      options={
                        industries?.map((e) => ({
                          id: e?.id,
                          title: e?.name
                        })) || []
                      }
                      show_location={false}
                      placeholder={t('Type to search…')}
                      onChange={(e) => {
                        const activeItem = String(
                          industries.find((industry) => industry.name === e)?.id
                        );
                        setFilterData((prev: any) => ({
                          ...prev,
                          industry: +activeItem
                        }));
                      }}
                      onInputChange={(e) => setIndustryQuery(e)}
                      values={industries}
                    />
                  </div>
                  <div className="inputContainer">
                    <span className="label">{t('City')}</span>
                    <Autocomplete
                      value={filterData?.location?.display_name || ''}
                      options={
                        cities?.map((e: City) => ({
                          id: e?.id,
                          title: e?.display_name
                        })) || []
                      }
                      show_location={true}
                      show_arrow={false}
                      placeholder={t('Type to search…')}
                      onChange={handleLocationChange}
                      onInputChange={(e) => setLocationQuery(e)}
                      fieldName="location"
                      values={cities}
                      loadMore={handleLoadMoreCities}
                    />
                    {locationError && (
                      <div className="error-message">{locationError}</div>
                    )}
                  </div>
                  <div className="inputContainer">
                    <span className="label">{t('Experience')}</span>
                    <DropDown
                      items={workExperience}
                      placeholder={t('Years')}
                      defaultValue={filterData?.required_experience || ''}
                      selectedValue={(e) =>
                        setFilterData((prev: any) => ({
                          ...prev,
                          required_experience: e
                        }))
                      }
                    />
                  </div>
                  <div className="inputContainer">
                    <span className="label">{t('Type')}</span>
                    <DropDown
                      placeholder={t('Full time')}
                      items={jobContracts}
                      defaultValue={filterData?.type || ''}
                      selectedValue={(e) =>
                        setFilterData((prev: any) => ({ ...prev, type: e }))
                      }
                    />
                  </div>
                  <div className="fieldsSalary">
                    <div className="salary_label">
                      <span className="label">{t('Salary range')}</span>
                      <span className="label">{`${value[0]}$ - ${value[1]}$`}</span>
                    </div>
                    <Slider
                      max={5000}
                      getAriaLabel={() => 'Temperature range'}
                      value={value}
                      onChange={(e, value: any) => {
                        setValue(value);
                        setFilterData((prev: any) => ({
                          ...prev,
                          range: value
                        }));
                      }}
                      valueLabelDisplay="auto"
                      style={{ color: colors.tenantColor }}
                    />
                  </div>
                  <div
                    className="clearContainer"
                    onClick={() => {
                      setFilteredJobs(publicJobs);
                      setFilterData({});
                      setValue([0, 5000]);
                    }}
                  >
                    <span>{t('Clear All')}</span>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      <div className="cardSection">
        <div className="cartContainerFlex">
          {cms?.show_jobs &&
            filteredJobs?.map((e: any, index: any) => (
              <JobOpeningCard item={e} key={index} />
            ))}
        </div>
        <Button
          className="next all"
          title={t('Show all jobs')}
          t={t}
          onClick={handleShowAllJobs}
        />
      </div>
      {openModal && (
        <PopUpWrapper onClick={handleModal}>
          <JobOpenings
            companyData={cms}
            setNewData={handleChange}
            isOneBtn={true}
            onClose={handleModal}
            showTopSide={false}
            isPopup={true}
            showTitle
          />
        </PopUpWrapper>
      )}
    </>
  );
}

export default JobOpening;
